export const SUBJECTS = ["Physics", "Chemistry", "Maths", "Biology", "Other"] as const;
export type Subject = (typeof SUBJECTS)[number];

export const REVISION_INTERVALS = [1, 3, 7, 15, 30] as const;

export type Profile = {
  id: string;
  fullName: string;
  username: string | null;
  photoUrl: string | null;
  isGuest: boolean;
  email: string | null;
  authProvider: string | null;
  isPremium: boolean;
  xp: number;
  level: number;
  currentStreak: number;
  longestStreak: number;
  joinedAt: string;
  defaultGoalMinutes: number;
  avatarId: string | null;
  studyClass: string | null;
  targetExam: string | null;
};

export type RevisionTask = {
  id: string;
  subject: string;
  chapter: string;
  revisionNumber: number;
  dueDate: string;
  status: "pending" | "completed";
  completedAt: string | null;
  quality: "forgot" | "average" | "good" | "perfect" | null;
};

export type Dashboard = {
  profile: Profile;
  todaySeconds: number;
  todaySessions: number;
  totalSeconds: number;
  totalSessions: number;
  goalMinutes: number;
  goalProgress: number;
  todayRevisions: RevisionTask[];
  week: { date: string; minutes: number }[];
};

export type FinishSessionResult = {
  sessionId: string;
  xpEarned: number;
  level: number;
  currentStreak: number;
  goalCompleted: boolean;
  goalJustCompleted: boolean;
};

export function localDateString(d = new Date()): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function formatClock(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function subjectAccent(subject: string): string {
  switch (subject) {
    case "Physics":
      return "from-emerald-600 to-green-700";
    case "Chemistry":
      return "from-emerald-500 to-teal-600";
    case "Maths":
      return "from-lime-600 to-emerald-700";
    case "Biology":
      return "from-lime-500 to-green-600";
    default:
      return "from-amber-500 to-orange-600";
  }
}

export function subjectTint(subject: string): string {
  switch (subject) {
    case "Physics":
      return "text-emerald-200";
    case "Chemistry":
      return "text-emerald-300";
    case "Maths":
      return "text-lime-200";
    case "Biology":
      return "text-lime-300";
    default:
      return "text-amber-300";
  }
}

export function formatPrettyDate(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}